import * as M from "../movement.mjs";
import { axialToWorld } from "../hex-layout.mjs";

const GVS = "#5ec8f0", BOE = "#f05a48";
const css = (hex) => `#${hex.toString(16).padStart(6, "0")}`;

// 2D overview for large boards: terrain, ore owners, units, bases and the camera's ground footprint.
// Tap or drag on it to move the camera.
export class Minimap {
  constructor(canvas, world) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.world = world;
    this.state = null;
    this.selected = new Set();
    this._queued = false;
    let dragging = false;
    const jump = (e) => {
      const r = canvas.getBoundingClientRect();
      const p = this._toWorld(((e.clientX - r.left) / r.width) * canvas.width, ((e.clientY - r.top) / r.height) * canvas.height);
      if (p) world.panTo(p.x, p.z);
      this.draw();
    };
    canvas.addEventListener("pointerdown", (e) => { dragging = true; canvas.setPointerCapture(e.pointerId); jump(e); });
    canvas.addEventListener("pointermove", (e) => { if (dragging) jump(e); });
    canvas.addEventListener("pointerup", () => { dragging = false; });
    world.controls.addEventListener("change", () => this.queue());
  }

  setBoard(state) {
    this.state = state;
    const b = this.world.bounds;
    const pad = 1.1, w = b.maxX - b.minX + pad * 2, d = b.maxZ - b.minZ + pad * 2;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cssW = this.canvas.clientWidth || 240;
    this.canvas.width = Math.round(cssW * dpr);
    this.canvas.height = Math.round(cssW * (d / w) * dpr);
    this.canvas.style.height = `${Math.round(cssW * (d / w))}px`;
    this.scale = this.canvas.width / w;
    this.ox = b.minX - pad;
    this.oz = b.minZ - pad;
    this.draw();
  }

  _toCanvas(x, z) { return [(x - this.ox) * this.scale, (z - this.oz) * this.scale]; }
  _toWorld(px, py) { return this.scale ? { x: px / this.scale + this.ox, z: py / this.scale + this.oz } : null; }

  queue() {
    if (this._queued) return;
    this._queued = true;
    requestAnimationFrame(() => { this._queued = false; this.draw(); });
  }

  draw(selected = this.selected) {
    this.selected = selected;
    const st = this.state, ctx = this.ctx;
    if (!st || !this.scale || this.canvas.hidden) return;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    const R = this.scale * 1.0;
    const hexPath = (x, y, rad) => {
      ctx.beginPath();
      for (let k = 0; k < 6; k++) {
        const a = Math.PI / 6 + (k * Math.PI) / 3;
        ctx[k ? "lineTo" : "moveTo"](x + rad * Math.cos(a), y + rad * Math.sin(a));
      }
      ctx.closePath();
    };
    for (const h of M.boardCells(st)) {
      const w = axialToWorld(h.q, h.r);
      const [x, y] = this._toCanvas(w.x, w.z);
      hexPath(x, y, R);
      ctx.fillStyle = css(this.world.tileColor(h.q, h.r));
      ctx.fill();
      const owner = st.oreOwner?.[M.key(h)];
      if (owner) { ctx.strokeStyle = owner === 1 ? GVS : BOE; ctx.lineWidth = Math.max(1, R * 0.35); ctx.stroke(); }
    }
    for (const b of st.bases) {
      if ((b.hp ?? 0) <= 0) continue;
      const w = axialToWorld(b.q, b.r), [x, y] = this._toCanvas(w.x, w.z), s = R * 2.2;
      ctx.fillStyle = b.owner === 1 ? GVS : BOE;
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = Math.max(1, R * 0.3);
      ctx.fillRect(x - s / 2, y - s / 2, s, s);
      ctx.strokeRect(x - s / 2, y - s / 2, s, s);
    }
    for (const u of st.units) {
      const w = axialToWorld(u.q, u.r), [x, y] = this._toCanvas(w.x, w.z);
      ctx.beginPath();
      ctx.arc(x, y, R * (u.type === "infantry" ? 0.55 : 0.75), 0, Math.PI * 2);
      ctx.fillStyle = u.owner === 1 ? GVS : BOE;
      ctx.fill();
      if (selected.has(u.id)) { ctx.strokeStyle = "#fff"; ctx.lineWidth = Math.max(1, R * 0.35); ctx.stroke(); }
    }
    const fp = this.world.footprint();
    if (fp) {
      ctx.beginPath();
      fp.forEach((p, i) => { const [x, y] = this._toCanvas(p.x, p.z); ctx[i ? "lineTo" : "moveTo"](x, y); });
      ctx.closePath();
      ctx.strokeStyle = "rgba(255,255,255,0.9)";
      ctx.lineWidth = Math.max(1, R * 0.3);
      ctx.stroke();
    }
  }
}
